/**
 * Knowledge Base Dashboard — 知识库管理
 *
 * Dashboard-first approach:
 *   1. Category summary cards (doc_type breakdown — click to filter)
 *   2. Time-based stats (today / this week / this month)
 *   3. Data owner breakdown (who uploaded what)
 *   4. Source breakdown (email / upload / system)
 *   5. RAG status overview (indexed / pending / failed)
 *   6. Filtered document list (click-through from any summary card)
 *   7. Upload is secondary action (not default view)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  FileText, FolderOpen, Upload, Search, RefreshCcw, Loader2,
  Brain, CheckCircle2, AlertTriangle, Clock, User, Mail, Globe,
  X, ChevronRight, Database, Sparkles, BarChart3, Filter, Tag, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ───────────────────────────────────────────────────────

interface DashboardData {
  overview: {
    totalFiles: number;
    totalSize: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  byDocType: Array<{ docType: string; count: number; totalSize: number }>;
  bySource: Array<{ source: string; count: number }>;
  byUploader: Array<{ uploadedBy: string; count: number }>;
  ragStatus: {
    indexed: number;
    pending: number;
    failed: number;
    totalChunks: number;
    totalTokens: number;
  };
  recentFiles: AttachmentRow[];
  topTags: Array<{ key: string; source: string; count: number }>;
}

interface AttachmentRow {
  id: string;
  file_name: string;
  doc_type: string | null;
  source: string;
  uploaded_by: string | null;
  rag_status: 'indexed' | 'failed' | null;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  rag_indexed_at: string | null;
  chunk_count: number | null;
  total_tokens: number | null;
  ai_doc_type?: string | null;
  ai_summary?: string | null;
  tags?: Array<{ key: string; source: string; confidence?: number }>;
}

interface PaginatedResponse {
  items: AttachmentRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-SG', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const DOC_TYPE_LABELS: Record<string, string> = {
  sop: 'SOP',
  rate_card: 'Rate Card',
  policy: 'Policy',
  contract: 'Contract',
  manual: 'Manual',
  report: 'Report',
  tariff: 'Tariff',
  customer_guide: 'Customer Guide',
  bill_of_lading: 'Bill of Lading',
  trucking_request: 'Trucking Request',
  job_order_pdf: 'Job Order',
  job_order_photo: 'Job Photo',
  invoice: 'Invoice',
  quotation: 'Quotation',
  vendor_quote: 'Vendor Quote',
  uncategorized: 'Uncategorized',
};

const SOURCE_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  upload: Upload,
  whatsapp: Globe,
  system: Database,
  ocr: Sparkles,
};

const RAG_STATUS_CONFIG = {
  indexed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2, label: 'Indexed' },
  failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: AlertTriangle, label: 'Failed' },
  pending: { color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: Clock, label: 'Pending' },
};

// ─── Component ───────────────────────────────────────────────────

export default function KnowledgeBasePage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'dashboard' | 'files'>('dashboard');

  // File list state
  const [files, setFiles] = useState<AttachmentRow[]>([]);
  const [filesTotal, setFilesTotal] = useState(0);
  const [filesPage, setFilesPage] = useState(1);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filterDocType, setFilterDocType] = useState<string | null>(null);
  const [filterSource, setFilterSource] = useState<string | null>(null);
  const [filterRag, setFilterRag] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // RAG ops
  const [indexingId, setIndexingId] = useState<string | null>(null);
  const [bulkIndexing, setBulkIndexing] = useState(false);

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tags
  const [filterTag, setFilterTag] = useState<string | null>(null);

  // ── Dashboard data ──
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<DashboardData>('/document-rag/dashboard');
      setDashboard(data);
    } catch {
      toast.error('Failed to load knowledge base dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadDashboard(); }, [loadDashboard]);

  // ── File list ──
  const loadFiles = useCallback(async (page = 1) => {
    setFilesLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (filterDocType) params.set('docType', filterDocType === 'uncategorized' ? '' : filterDocType);
      if (filterSource) params.set('source', filterSource);
      if (filterRag === 'indexed') params.set('parseStatus', 'completed'); // approximate — we filter client-side for rag
      const res = await api.get<PaginatedResponse>(`/attachments?${params}`);
      // Client-side RAG filter (since backend doesn't have ragStatus filter yet)
      let items = res.items ?? [];
      if (filterRag === 'indexed') items = items.filter(f => f.rag_status === 'indexed');
      else if (filterRag === 'failed') items = items.filter(f => f.rag_status === 'failed');
      else if (filterRag === 'pending') items = items.filter(f => !f.rag_status);
      setFiles(items);
      setFilesTotal(res.total ?? 0);
      setFilesPage(page);
    } catch {
      toast.error('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  }, [filterDocType, filterSource, filterRag]);

  useEffect(() => {
    if (tab === 'files') void loadFiles(1);
  }, [tab, loadFiles]);

  // ── RAG operations ──
  const handleIndexSingle = async (id: string) => {
    setIndexingId(id);
    try {
      await api.post<any>(`/document-rag/index/${id}`, {});
      toast.success('Document indexed');
      void loadFiles(filesPage);
      void loadDashboard();
    } catch {
      toast.error('Indexing failed');
    } finally {
      setIndexingId(null);
    }
  };

  const handleBulkIndex = async () => {
    setBulkIndexing(true);
    try {
      const res = await api.post<{ indexed: number; failed: number }>('/document-rag/index/bulk', { limit: 50 });
      toast.success(`Indexed: ${res.indexed}, Failed: ${res.failed}`);
      void loadFiles(filesPage);
      void loadDashboard();
    } catch {
      toast.error('Bulk indexing failed');
    } finally {
      setBulkIndexing(false);
    }
  };

  const handleRemoveIndex = async (id: string) => {
    try {
      await api.delete<any>(`/document-rag/index/${id}`);
      toast.success('Index removed');
      void loadFiles(filesPage);
      void loadDashboard();
    } catch {
      toast.error('Failed to remove index');
    }
  };

  // ── Tag operations ──
  const handleAddTag = async (id: string, tagKey: string) => {
    try {
      await api.post<any>(`/document-rag/tags/${id}`, { tags: [tagKey] });
      toast.success('Tag added');
      void loadFiles(filesPage);
      void loadDashboard();
    } catch {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (id: string, tagKey: string) => {
    try {
      await api.delete<any>(`/document-rag/tags/${id}/${encodeURIComponent(tagKey)}`);
      toast.success('Tag removed');
      void loadFiles(filesPage);
      void loadDashboard();
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  // ── Upload ──
  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        // 1. Get presigned URL
        const { uploadUrl, key } = await api.post<{ uploadUrl: string; key: string }>('/upload/presigned', {
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
        });
        // 2. Upload to R2
        await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
        // 3. Create attachment record
        await api.post('/attachments', {
          entityType: 'document',
          entityId: '00000000-0000-4000-a000-000000000000', // org-level document
          source: 'upload',
          fileName: file.name,
          fileUrl: key,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });
      }
      toast.success(`Uploaded ${fileList.length} file(s)`);
      setShowUpload(false);
      void loadDashboard();
      if (tab === 'files') void loadFiles(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Click handlers from dashboard cards ──
  const drillToDocType = (docType: string) => {
    setFilterDocType(docType);
    setFilterSource(null);
    setFilterRag(null);
    setFilterTag(null);
    setTab('files');
  };

  const drillToSource = (source: string) => {
    setFilterSource(source);
    setFilterDocType(null);
    setFilterRag(null);
    setFilterTag(null);
    setTab('files');
  };

  const drillToRag = (status: string) => {
    setFilterRag(status);
    setFilterDocType(null);
    setFilterSource(null);
    setFilterTag(null);
    setTab('files');
  };

  const drillToTag = (tagKey: string) => {
    setFilterTag(tagKey);
    setFilterDocType(null);
    setFilterSource(null);
    setFilterRag(null);
    setTab('files');
  };

  const clearFilters = () => {
    setFilterDocType(null);
    setFilterSource(null);
    setFilterRag(null);
    setFilterTag(null);
    setSearchText('');
  };

  const hasFilter = filterDocType || filterSource || filterRag || filterTag;

  // ── Render ─────────────────────────────────────────────────────

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const d = dashboard;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Knowledge Base
          </h1>
          <p className="text-sm text-muted-foreground">
            AI-powered document management — organize, index, and search your business knowledge
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { void loadDashboard(); if (tab === 'files') void loadFiles(filesPage); }}>
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowUpload(!showUpload)}>
            <Upload className="h-3.5 w-3.5 mr-1" /> Upload
          </Button>
          <Button size="sm" onClick={handleBulkIndex} disabled={bulkIndexing}>
            {bulkIndexing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            Index All
          </Button>
        </div>
      </div>

      {/* Upload Panel (hidden by default) */}
      {showUpload && (
        <Card>
          <CardContent className="p-4">
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Drop files here or click to upload</span>
              <span className="text-[10px] text-muted-foreground">SOP, Rate Card, Policy, Report, Invoice, BL, or any business document</span>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.txt"
                onChange={e => { void handleUpload(e.target.files); e.target.value = ''; }}
              />
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </label>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={v => setTab(v as 'dashboard' | 'files')}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-1"><BarChart3 className="h-3.5 w-3.5" /> Overview</TabsTrigger>
          <TabsTrigger value="files" className="gap-1"><FolderOpen className="h-3.5 w-3.5" /> All Files</TabsTrigger>
        </TabsList>

        {/* ═══ DASHBOARD TAB ═══ */}
        <TabsContent value="dashboard" className="mt-3 space-y-4">
          {d && (
            <>
              {/* Row 1: Overview KPIs */}
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
                <KpiCard label="Total Files" value={d.overview.totalFiles} sub={formatBytes(d.overview.totalSize)} icon={FileText} />
                <KpiCard label="Today" value={d.overview.today} icon={Clock} />
                <KpiCard label="This Week" value={d.overview.thisWeek} icon={BarChart3} />
                <KpiCard label="This Month" value={d.overview.thisMonth} icon={BarChart3} />
              </div>

              {/* Row 2: RAG Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Brain className="h-4 w-4" /> AI Knowledge Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 grid-cols-3 mb-3">
                    <button className="text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors" onClick={() => drillToRag('indexed')}>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="text-2xl font-bold">{d.ragStatus.indexed}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Indexed</p>
                    </button>
                    <button className="text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors" onClick={() => drillToRag('pending')}>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-2xl font-bold">{d.ragStatus.pending}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Pending</p>
                    </button>
                    <button className="text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors" onClick={() => drillToRag('failed')}>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-2xl font-bold">{d.ragStatus.failed}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Failed</p>
                    </button>
                  </div>
                  {d.ragStatus.indexed > 0 && (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground border-t pt-2">
                      <span>{d.ragStatus.totalChunks} chunks</span>
                      <span>{d.ragStatus.totalTokens.toLocaleString()} tokens</span>
                      <Progress value={d.overview.totalFiles > 0 ? (d.ragStatus.indexed / d.overview.totalFiles) * 100 : 0} className="flex-1 h-2" />
                      <span>{d.overview.totalFiles > 0 ? Math.round((d.ragStatus.indexed / d.overview.totalFiles) * 100) : 0}% indexed</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Row 3: Categories + Source + Owner */}
              <div className="grid gap-4 lg:grid-cols-3">
                {/* By Doc Type */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" /> By Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {d.byDocType.map(item => (
                        <button
                          key={item.docType}
                          className="w-full flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors"
                          onClick={() => drillToDocType(item.docType)}
                        >
                          <span className="truncate">{DOC_TYPE_LABELS[item.docType] ?? item.docType}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-[10px] tabular-nums">{item.count}</Badge>
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          </div>
                        </button>
                      ))}
                      {d.byDocType.length === 0 && (
                        <p className="text-xs text-muted-foreground py-2 text-center">No documents yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* By Source */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Globe className="h-4 w-4" /> By Source
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {d.bySource.map(item => {
                        const Icon = SOURCE_ICONS[item.source] ?? FileText;
                        return (
                          <button
                            key={item.source}
                            className="w-full flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors"
                            onClick={() => drillToSource(item.source)}
                          >
                            <span className="flex items-center gap-2">
                              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                              {item.source === 'upload' ? 'Manual Upload' : item.source === 'email' ? 'Email Received' : item.source === 'system' ? 'System Generated' : item.source}
                            </span>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-[10px] tabular-nums">{item.count}</Badge>
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* By Owner */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" /> By Owner
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {d.byUploader.map(item => (
                        <div
                          key={item.uploadedBy}
                          className="flex items-center justify-between rounded px-2 py-1.5 text-sm"
                        >
                          <span className="truncate">{item.uploadedBy}</span>
                          <Badge variant="secondary" className="text-[10px] tabular-nums">{item.count}</Badge>
                        </div>
                      ))}
                      {d.byUploader.length === 0 && (
                        <p className="text-xs text-muted-foreground py-2 text-center">No owner data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Row 4: AI Tag Cloud */}
              {d.topTags && d.topTags.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Tag className="h-4 w-4" /> AI Tags
                      <span className="text-xs font-normal text-muted-foreground ml-1">Auto-classified by AI during indexing</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {d.topTags.map(t => (
                        <button
                          key={t.key}
                          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] hover:bg-muted/50 transition-colors"
                          onClick={() => drillToTag(t.key)}
                        >
                          {t.source === 'ai' && <Sparkles className="h-2.5 w-2.5 text-amber-500" />}
                          <span>{t.key}</span>
                          <span className="text-muted-foreground tabular-nums">({t.count})</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Row 5: Recent Files */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Recent Files</span>
                    <Button size="sm" variant="ghost" onClick={() => setTab('files')}>
                      View All <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FileTable
                    files={d.recentFiles}
                    indexingId={indexingId}
                    onIndex={handleIndexSingle}
                    onRemoveIndex={handleRemoveIndex}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    compact
                  />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ═══ FILES TAB ═══ */}
        <TabsContent value="files" className="mt-3 space-y-3">
          {/* Active Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            {hasFilter && (
              <div className="flex items-center gap-1">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                {filterDocType && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    {DOC_TYPE_LABELS[filterDocType] ?? filterDocType}
                    <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setFilterDocType(null)} />
                  </Badge>
                )}
                {filterSource && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    {filterSource}
                    <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setFilterSource(null)} />
                  </Badge>
                )}
                {filterRag && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    RAG: {filterRag}
                    <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setFilterRag(null)} />
                  </Badge>
                )}
                {filterTag && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Tag className="h-2.5 w-2.5" /> {filterTag}
                    <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setFilterTag(null)} />
                  </Badge>
                )}
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={clearFilters}>
                  Clear all
                </Button>
              </div>
            )}
            <div className="ml-auto text-xs text-muted-foreground tabular-nums">
              {filesTotal} files
            </div>
          </div>

          {/* File List */}
          {filesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <FileTable
                files={files.filter(f => {
                  if (searchText && !f.file_name?.toLowerCase().includes(searchText.toLowerCase())) return false;
                  if (filterTag && !(f.tags ?? []).some(t => t.key === filterTag)) return false;
                  return true;
                })}
                indexingId={indexingId}
                onIndex={handleIndexSingle}
                onRemoveIndex={handleRemoveIndex}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
              />
              {/* Pagination */}
              {filesTotal > 25 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button size="sm" variant="outline" disabled={filesPage <= 1} onClick={() => void loadFiles(filesPage - 1)}>
                    Previous
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Page {filesPage} of {Math.ceil(filesTotal / 25)}
                  </span>
                  <Button size="sm" variant="outline" disabled={!files.length || files.length < 25} onClick={() => void loadFiles(filesPage + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon }: { label: string; value: number; sub?: string; icon: typeof FileText }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="rounded-lg bg-muted p-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
          <p className="text-[11px] text-muted-foreground">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function FileTable({
  files,
  indexingId,
  onIndex,
  onRemoveIndex,
  onAddTag,
  onRemoveTag,
  compact,
}: {
  files: AttachmentRow[];
  indexingId: string | null;
  onIndex: (id: string) => void;
  onRemoveIndex: (id: string) => void;
  onAddTag: (id: string, tagKey: string) => void;
  onRemoveTag: (id: string, tagKey: string) => void;
  compact?: boolean;
}) {
  const [tagInputId, setTagInputId] = useState<string | null>(null);
  const [tagInputValue, setTagInputValue] = useState('');

  if (files.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        <FileText className="mx-auto h-8 w-8 mb-2 opacity-30" />
        No files found
      </div>
    );
  }

  const submitTag = (id: string) => {
    const v = tagInputValue.trim().toLowerCase().replace(/\s+/g, '-');
    if (v) {
      onAddTag(id, v);
      setTagInputValue('');
      setTagInputId(null);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="text-left p-2 pl-3">File</th>
            <th className="text-left p-2">Category</th>
            <th className="text-left p-2">Source</th>
            {!compact && <th className="text-left p-2">Tags</th>}
            <th className="text-left p-2">RAG</th>
            {!compact && <th className="text-left p-2">Size</th>}
            <th className="text-left p-2">Date</th>
            <th className="text-right p-2 pr-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {files.map(f => {
            const ragKey = f.rag_status ?? 'pending';
            const ragConf = RAG_STATUS_CONFIG[ragKey as keyof typeof RAG_STATUS_CONFIG] ?? RAG_STATUS_CONFIG.pending;
            const RagIcon = ragConf.icon;
            const isIndexing = indexingId === f.id;
            const SourceIcon = SOURCE_ICONS[f.source] ?? FileText;
            const aiType = f.ai_doc_type ?? f.doc_type;
            const tags = f.tags ?? [];

            return (
              <tr key={f.id} className="hover:bg-muted/30 transition-colors">
                <td className="p-2 pl-3">
                  <div className="flex flex-col gap-0.5 max-w-[260px]">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{f.file_name ?? 'Untitled'}</span>
                    </div>
                    {f.ai_summary && (
                      <p className="text-[10px] text-muted-foreground truncate pl-5" title={f.ai_summary}>
                        {f.ai_summary}
                      </p>
                    )}
                  </div>
                </td>
                <td className="p-2">
                  <Badge variant="outline" className="text-[10px]">
                    {f.ai_doc_type && (
                      <Sparkles className="h-2 w-2 mr-0.5 text-amber-500 inline" />
                    )}
                    {DOC_TYPE_LABELS[aiType ?? 'uncategorized'] ?? aiType ?? 'Uncategorized'}
                  </Badge>
                </td>
                <td className="p-2">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <SourceIcon className="h-3 w-3" />
                    {f.source}
                  </span>
                </td>
                {!compact && (
                  <td className="p-2">
                    <div className="flex flex-wrap items-center gap-1 max-w-[200px]">
                      {tags.slice(0, 4).map(t => (
                        <Badge
                          key={t.key}
                          variant={t.source === 'ai' ? 'secondary' : 'default'}
                          className="text-[9px] gap-0.5 group cursor-default"
                        >
                          {t.source === 'ai' && <Sparkles className="h-2 w-2 text-amber-500" />}
                          {t.key}
                          <X
                            className="h-2 w-2 opacity-0 group-hover:opacity-100 cursor-pointer"
                            onClick={() => onRemoveTag(f.id, t.key)}
                          />
                        </Badge>
                      ))}
                      {tags.length > 4 && (
                        <span className="text-[9px] text-muted-foreground">+{tags.length - 4}</span>
                      )}
                      {tagInputId === f.id ? (
                        <input
                          className="h-5 w-20 text-[10px] border rounded px-1 bg-background"
                          placeholder="add tag…"
                          autoFocus
                          value={tagInputValue}
                          onChange={e => setTagInputValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') submitTag(f.id);
                            if (e.key === 'Escape') { setTagInputId(null); setTagInputValue(''); }
                          }}
                          onBlur={() => { setTagInputId(null); setTagInputValue(''); }}
                        />
                      ) : (
                        <button
                          className="h-4 w-4 rounded hover:bg-muted flex items-center justify-center"
                          onClick={() => setTagInputId(f.id)}
                          title="Add tag"
                        >
                          <Plus className="h-2.5 w-2.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
                <td className="p-2">
                  <Badge className={`text-[10px] gap-1 ${ragConf.color}`}>
                    <RagIcon className="h-2.5 w-2.5" />
                    {ragConf.label}
                    {f.chunk_count ? ` (${f.chunk_count})` : ''}
                  </Badge>
                </td>
                {!compact && (
                  <td className="p-2 text-xs text-muted-foreground tabular-nums">
                    {f.file_size ? formatBytes(f.file_size) : '—'}
                  </td>
                )}
                <td className="p-2 text-xs text-muted-foreground tabular-nums">
                  {formatDate(f.created_at)}
                </td>
                <td className="p-2 pr-3 text-right">
                  {f.rag_status === 'indexed' ? (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-500" onClick={() => onRemoveIndex(f.id)}>
                      Remove
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onIndex(f.id)} disabled={isIndexing}>
                      {isIndexing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                      Index
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
